import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
type PricingTier = {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  highlighted?: boolean;
};
const tiers: PricingTier[] = [{
  name: 'Solo',
  price: '$89',
  period: '/month',
  description: 'Perfect for dedicated individuals',
  features: ['8 classes per month', 'Access to all disciplines', 'Open studio hours', 'Performance workshops']
}, {
  name: 'Ensemble',
  price: '$159',
  period: '/month',
  description: 'For the committed artist',
  features: ['Unlimited classes', 'Priority booking', 'Private coaching sessions', 'Showcase opportunities', 'Guest masterclasses'],
  highlighted: true
}, {
  name: 'Principal',
  price: '$299',
  period: '/month',
  description: 'The complete experience',
  features: ['Everything in Ensemble', 'Weekly private lessons', 'Choreography mentorship', 'Professional video sessions', 'Industry networking events', 'Competition preparation']
}];
function PricingCard({
  tier,
  index
}: {
  tier: PricingTier;
  index: number;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({
    x: 50,
    y: 50
  });
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width * 100;
    const y = (e.clientY - rect.top) / rect.height * 100;
    setMousePosition({
      x,
      y
    });
  };
  return <motion.div ref={cardRef} initial={{
    opacity: 0,
    y: 60
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.8,
    delay: index * 0.15
  }} onMouseMove={handleMouseMove} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} className={`relative p-px ${tier.highlighted ? 'lg:-mt-8 lg:mb-8' : ''}`} data-id="element-49">
      {/* Spotlight effect on hover */}
      <div className="absolute inset-0 transition-opacity duration-500 pointer-events-none" style={{
      opacity: isHovered ? 1 : 0,
      background: `radial-gradient(circle 300px at ${mousePosition.x}% ${mousePosition.y}%, rgba(255,255,255,0.15) 0%, transparent 60%)`
    }} data-id="element-50" />

      {/* Card border */}
      <div className={`absolute inset-0 ${tier.highlighted ? 'border-2 border-white/40' : 'border border-white/20'}`} data-id="element-51" />

      {/* Card content */}
      <div className="relative bg-black p-8 md:p-10 h-full" data-id="element-52">
        {tier.highlighted && <div className="absolute -top-4 left-1/2 -translate-x-1/2" data-id="element-53">
            <span className="bg-white text-black px-4 py-1 text-xs tracking-wider uppercase font-medium" data-id="element-54">
              Most Popular
            </span>
          </div>}

        <div className="mb-8" data-id="element-55">
          <h3 className="font-display text-3xl font-bold mb-2" data-id="element-56">{tier.name}</h3>
          <p className="text-white/40 text-sm" data-id="element-57">{tier.description}</p>
        </div>

        <div className="mb-8" data-id="element-58">
          <span className="font-display text-6xl font-bold" data-id="element-59">{tier.price}</span>
          <span className="text-white/40 text-lg" data-id="element-60">{tier.period}</span>
        </div>

        <div className="h-px bg-white/10 mb-8" data-id="element-61" />

        <ul className="space-y-4 mb-10" data-id="element-62">
          {tier.features.map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-63">
              <CheckIcon className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" data-id="element-64" />
              <span className="text-white/80" data-id="element-65">{feature}</span>
            </li>)}
        </ul>

        <motion.button whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} className={`w-full py-4 font-medium tracking-wider uppercase text-sm transition-colors duration-300 ${tier.highlighted ? 'bg-[#DC2626] text-white hover:bg-[#b91c1c]' : 'border border-white/30 text-white hover:border-white/60 hover:bg-white/5'}`} data-id="element-66">
          Select Plan
        </motion.button>
      </div>
    </motion.div>;
}
export function PricingCards() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="relative w-full bg-black py-32 overflow-hidden" data-id="element-67">
      {/* Stage lighting from above */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse 80% 40% at 50% 0%, rgba(255,255,255,0.05) 0%, transparent 50%)'
    }} data-id="element-68" />

      <div className="max-w-7xl mx-auto px-6" data-id="element-69">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} className="text-center mb-20" data-id="element-70">
          <span className="text-white/40 text-xs tracking-[0.5em] uppercase block mb-6" data-id="element-71">
            Membership
          </span>
          <h2 className="font-display text-5xl md:text-7xl font-bold leading-[0.9] mb-6" data-id="element-72">
            Take The
            <span className="block italic text-white/30" data-id="element-73">Stage</span>
          </h2>
          <p className="text-white/50 text-lg max-w-xl mx-auto" data-id="element-74">
            Choose your spotlight. Every membership unlocks a world of artistic
            growth and performance excellence.
          </p>
        </motion.div>

        {/* Pricing grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-start" data-id="element-75">
          {tiers.map((tier, index) => <PricingCard key={tier.name} tier={tier} index={index} data-id="element-76" />)}
        </div>

        {/* Additional info */}
        <motion.p initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.6,
        delay: 0.8
      }} className="text-center text-white/30 text-sm mt-12" data-id="element-77">
          All memberships include a complimentary trial class. Cancel anytime.
        </motion.p>
      </div>

      {/* Bottom divider */}
      <div className="absolute bottom-0 left-0 right-0" data-id="element-78">
        <div className="geometric-line w-full" data-id="element-79" />
      </div>
    </section>;
}